import robustPointInPolygon from 'robust-point-in-polygon';
import { Vector3 } from 'three';

import { memoizeWeak } from '@spazious/dollhouse-utils';

import { projectPointToAxis } from './projectPointToAxis.js';

/**
 * Returns the contours and their holes.
 * @param contours - The array of contours.
 * @returns The contours with their holes.
 */
export const computeContoursHoles = (contours: Vector3[][]) => {
  const contourMap = new Map<Vector3[], Vector3[][]>();
  // Create a copy for mutate on the fly and increase performance
  const copy = contours.slice();

  const computeNormal = memoizeWeak((contour: Vector3[]): Vector3 => {
    return new Vector3()
      .subVectors(contour[1], contour[0])
      .cross(new Vector3().subVectors(contour[2], contour[0]))
      .normalize();
  });

  const computeProjection = memoizeWeak((contour: Vector3[], normal: Vector3) => {
    return contour.map((point) => projectPointToAxis(point, normal).toArray());
  });

  for (let i = 0; i < copy.length; i++) {
    for (let j = 0; j < copy.length; j++) {
      if (i === j) continue;

      const contourI = copy[i];
      const contourJ = copy[j];

      const normalJ = computeNormal(contourJ);
      const projectedContourI = computeProjection(contourI, normalJ);
      const projectedContourJ = computeProjection(contourJ, normalJ);

      const allPointsInside = projectedContourI.every(
        (point) => robustPointInPolygon(projectedContourJ, point) < 1
      );

      if (allPointsInside) {
        const contourHoles = contourMap.get(contourJ) ?? ([] as Vector3[][]);
        contourHoles.push(contourI);
        contourMap.set(contourJ, contourHoles);
        copy.splice(i, 1);
        i--;
        break;
      }
    }
  }

  // Prepare the result array
  const result = Array.from(contourMap.entries()) as [Vector3[], Vector3[][]][];

  // Add contours without holes
  for (const contour of copy) {
    if (!contourMap.has(contour)) {
      result.push([contour, []]);
    }
  }

  return result;
};
